import { findAttr } from '~/helpers/utils';
import type { TAttributes } from '~/components/Product/types/product';
import { Attributes } from '~/components/Product/types/product';
import type { ProductVariant } from '~/types/GraphQL';

/* eslint-disable camelcase */

export const getAttributeValue = (attribute: any) => {
  /**
   * List of attribute types: https://docs.commercetools.com/api/projects/productTypes#attributetype
   */
  switch (attribute.attributeDefinition?.type?.name) {
    case 'text':
    case 'ltext':
    case 'boolean':
    case 'number':
    case 'date':
    case 'time':
    case 'datetime':
    case 'money':
    case 'set':
      return attribute.value;

    case 'lenum':
    case 'enum':
      return attribute.value.key;

    case 'reference':
      return { typeId: attribute.value.typeId, id: attribute.value.id };

    default:
      return attribute?.value || undefined;
  }
};

export const formatAttributeList = (attributes: Array<any>): any[] =>
  attributes.map((attr) => {
    const attrValue = getAttributeValue(attr);
    return {
      name: attr.name,
      value: attrValue,
      // eslint-disable-next-line no-underscore-dangle
      label: attr._translated,
    };
  });

export default {
  getAttributes: (products: ProductVariant[] | ProductVariant, filterByAttributeName?: string[]): Record<string, any | string> => {
    const isSingleProduct = !Array.isArray(products);
    const productList = (isSingleProduct ? [products] : products) as ProductVariant[];

    if (!products || productList.length === 0) {
      return {} as any;
    }

    const formatAttributes = (product: ProductVariant): any[] =>
      formatAttributeList(product.attributesRaw).filter(attribute => filterByAttributeName ? filterByAttributeName.includes(attribute.name) : attribute);

    const reduceToUniques = (prev, curr) => {
      const isAttributeExist = prev.some(el => el.name === curr.name && el.value === curr.value);

      if (!isAttributeExist) {
        return [...prev, curr];
      }

      return prev;
    };

    const reduceByAttributeName = (prev, curr) => ({
      ...prev,
      [curr.name]: isSingleProduct
        ? curr.value
        : [
            ...(prev[curr.name] || []),
            {
              value: curr.value,
              label: curr.label,
            },
          ],
    });

    return productList
      .map(product => formatAttributes(product))
      .reduce((prev, curr) => [...prev, ...curr], [])
      .reduce(reduceToUniques, [])
      .reduce(reduceByAttributeName, {});
  },
  getFormattedPrice: ({ centAmount, fractionDigits = 2 }: Pick<any, 'centAmount' | 'fractionDigits'> = { centAmount: 0, fractionDigits: 2 }) => centAmount * 10 ** -fractionDigits,
  getName: item => item?.variant?.name ?? item?.name,
  /**
   * @deprecated
   */
  getUrl: (item, { locale }) => {
    if (!item) {
      return '';
    }
    const { name, variant, productKey } = item;

    const detailName = findAttr(variant?.attributesRaw, 'detailname')?.[locale];
    const colorName = findAttr(variant?.attributesRaw, 'color')?.label?.[locale];
    const colorCode = findAttr(variant?.attributesRaw, 'color_code');

    const path = [name, detailName, colorName, colorCode]
      .filter(Boolean)
      .map(urlPart => `${urlPart}`.trim().split(' '));

    return `/p/${encodeURIComponent(path.join('-'))}/${productKey}`;
  },
  getMaxAvailability: (item: any) => {
    const maxQty = 100;
    const stock = item?.availability?.noChannel?.availableQuantity || 0;
    return Math.min(stock, maxQty);
  },
  /**
   * after all refactors we will start always using this getter to calculate the product url
   * @param item
   */
  getProductUrl: (product, locale, queryParams = {}) => {
    if (!product) {
      return '';
    }

    const {
      key,
      hrefLang,
    } = product;

    let url = `/p/${encodeURIComponent(hrefLang[locale])}/${key}`;

    if (Object.keys(queryParams).length) {
      url += `?${new URLSearchParams(queryParams)}`;
    }

    return url;
  },
  getProductUrlByVariant: (product, locale, queryParams = {}) => {
    if (!product) {
      return '';
    }

    const {
      variant,
      key,
    } = product;

    let url = `/p/${encodeURIComponent(variant.hrefLang[locale])}/${key}`;

    if (Object.keys(queryParams).length) {
      url += `?${new URLSearchParams(queryParams)}`;
    }

    return url;
  },
  getEfficiencyIconUrl: (attributes: TAttributes) => {
    const { energy_efficiency_class } = attributes || {};

    if (!energy_efficiency_class) {
      return undefined;
    }

    return `/productpage/efficiency/${energy_efficiency_class}_right.png`;
  },
  getTrackingProduct: (product, currency, breadcrumbsToDisplay, quantity) => ({
    item_id: product?.key ?? '',
    currency,
    index: 0,
    ...breadcrumbsToDisplay,
    item_color: product?.attributes?.[Attributes.COLOR] ?? '',
    item_group: product?.attributes?.[Attributes.WGR] ?? '',
    item_ekb: product?.attributes?.[Attributes.EKB] ?? '',
    item_name: product?.name ?? '',
    item_product: product?.key ?? '', // product key
    price: product?.price?.final,
    quantity,
  }),
};
